import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Student view: a teacher tries the site as a student does, signed in as their
// class's TEST student (made by student-accounts' student_view action, and
// left out of the roster, progress and leaderboards: classroom_members.is_test).
//
// The teacher's own session is kept here while they're in it, and put back by
// "Back to teacher". It's kept in localStorage (not sessionStorage) so a closed
// tab can still get back.
// ─────────────────────────────────────────────────────────────────────────────

const KEY = 'classroom.teacherSession';
type Kept = { access_token: string; refresh_token: string; classroomId: string };

export function inStudentView(): boolean {
  try { return !!localStorage.getItem(KEY); } catch { return false; }
}

/** Switch to the class's test student (made the first time), then reload as them. */
export async function enterStudentView(classroomId: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const s = data.session;
  if (!s) throw new Error('Sign in first.');
  const { data: res, error } = await supabase.functions.invoke('student-accounts', { body: { action: 'student_view', classroomId } });
  if (error || !res?.token_hash) {
    let message = 'Couldn’t open student view. Try again.';
    try { const body = await (error as { context?: Response })?.context?.json(); if (body?.error) message = body.error; } catch { /* keep it */ }
    throw new Error(message);
  }
  try { localStorage.setItem(KEY, JSON.stringify({ access_token: s.access_token, refresh_token: s.refresh_token, classroomId } satisfies Kept)); }
  catch { throw new Error('This browser won’t keep your session, so student view can’t open here.'); }
  const { error: vErr } = await supabase.auth.verifyOtp({ token_hash: res.token_hash, type: 'magiclink' });
  if (vErr) {
    try { localStorage.removeItem(KEY); } catch { /* fine */ }
    // Still signed in as the teacher: nothing to undo.
    throw new Error('Couldn’t open student view. Try again.');
  }
  window.location.assign('/');
}

/** Back to the teacher's own session (or the sign-in page if it has run out). */
export async function leaveStudentView(): Promise<void> {
  let kept: Kept | null = null;
  try { kept = JSON.parse(localStorage.getItem(KEY) ?? 'null'); } catch { /* none */ }
  try { localStorage.removeItem(KEY); } catch { /* fine */ }
  await supabase.auth.signOut({ scope: 'local' });
  if (kept) {
    const { error } = await supabase.auth.setSession({ access_token: kept.access_token, refresh_token: kept.refresh_token });
    if (!error) { window.location.assign('/'); return; }
  }
  window.location.assign('/');   // signed out: the sign-in page
}
